import { lazy, Suspense, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isUserAuthorized, getCurrentTheme } from "./store/selectors";
import {
  checkToken,
  fetchUserProfile,
  loadInitialData,
} from "./actions/authActions";
import { Loading } from "./layout/utils/AppLoading";
import withClearCache from "./ClearCache";
import { ConfigProvider, theme } from "antd";
import es_ES from "antd/es/locale/es_ES";
import { THEME } from "./constants";
import { getCookie } from "./utils";
import { lightTheme, darkTheme } from "./constants/colors";

const MainContainer = lazy(() => import("./containers/MainContainer"));
const AuthContainer = lazy(() => import("./containers/AuthContainer"));

const App = () => {
  const dispatch = useDispatch();
  const currentTheme = useSelector(getCurrentTheme);

  const { auth } = useSelector(isUserAuthorized) || {};


  const ClearCacheComponent = withClearCache(() => {
    return <></>;
  });

  const fetchInitialData = useCallback(async () => {
    console.log("Fetching initial data")
    dispatch(fetchUserProfile());
    dispatch(loadInitialData());
    console.log("Finish fetching initial data")
  }, [dispatch]);

  const checkPersistency = useCallback(() => {
    dispatch(checkToken());
  }, [dispatch]);

  useEffect(() => {
    auth ? fetchInitialData() : checkPersistency();
  }, [auth, fetchInitialData, checkPersistency]);

  useEffect(() => {
    const refreshTheme = async () => {
      await dispatch({ type: THEME, payload: await getCookie(THEME) })
    }
    refreshTheme()
  }, [dispatch])

  useEffect(() => {
    const tokens = currentTheme ? lightTheme : darkTheme;
    Object.entries(tokens).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
    document.body.style.backgroundColor = tokens["--bg-page"];
  }, [currentTheme]);

  const themeTokens = currentTheme ? lightTheme : darkTheme;
  const primaryColor = themeTokens["--primary"];
  const primaryHoverColor = themeTokens["--primary-hover"];
  const actionHoverBackground = themeTokens["--action-hover-bg"];
  const rowHoverBackground = themeTokens["--row-hover-bg"];

  return (
    <Suspense fallback={<Loading centered />}>
      <ConfigProvider
        locale={es_ES}
        theme={{
          token: {
            borderRadius: 5,
            colorPrimary: primaryColor,
            colorPrimaryActive: primaryHoverColor,
            colorPrimaryHover: primaryHoverColor,
            colorLinkHover: primaryHoverColor,
            fontFamily: "Charlie Display",
            colorBgBase: currentTheme ? "#fff" : "#38393F",
            colorTextDisabled: currentTheme ? "rgba(0, 0, 0, 0.50)" : "rgba(255, 255, 255, 0.50)",
          },
          components: {
            Button: {
              defaultHoverBg: actionHoverBackground,
              defaultHoverBorderColor: primaryHoverColor,
              defaultHoverColor: primaryHoverColor,
            },
            Switch: {
              colorPrimary: primaryColor,
              colorPrimaryHover: primaryHoverColor,
            },
            Table: {
              rowHoverBg: rowHoverBackground,
            },
          },
          algorithm: currentTheme ? theme.defaultAlgorithm : theme.darkAlgorithm,
        }}
      >
        <ClearCacheComponent />
        {auth ? <MainContainer /> : <AuthContainer />}
      </ConfigProvider>
    </Suspense>
  );
};

export default App;
